import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! 👋 Welcome to Fair Rideshare. I'm here to help answer your questions about our rental services. What would you like to know?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    // Pricing questions
    if (msg.includes('price') || msg.includes('cost') || msg.includes('rate') || msg.includes('how much') || msg.includes('pricing')) {
      return "Our rental rate is $275 per week for all vehicles. There's also a $200 refundable security deposit required at the start of your rental.";
    }
    
    // Deposit questions
    if (msg.includes('deposit')) {
      return "We require a $200 refundable security deposit. This is returned to you at the end of your rental period, assuming no damages to the vehicle.";
    }
    
    // Vehicle/fleet questions
    if (msg.includes('vehicle') || msg.includes('car') || msg.includes('fleet') || msg.includes('available') || msg.includes('what do you have')) {
      return "We currently have 4 vehicles available:\n\n• 2015 Ford Fusion (Sedan)\n• 2013 Ford Fusion (Sedan)\n• 2013 Jeep Grand Cherokee (SUV)\n• 2017 Lincoln MKZ (Sedan)\n\nAll vehicles are $275/week. Would you like to book one?";
    }
    
    // Age requirements
    if (msg.includes('age') || msg.includes('old') || msg.includes('requirement') || msg.includes('qualify') || msg.includes('eligible')) {
      return "To rent from Fair Rideshare, you must be at least 21 years old and have a valid driver's license.";
    }
    
    // Payment questions
    if (msg.includes('pay') || msg.includes('payment') || msg.includes('cash app') || msg.includes('cashapp')) {
      return "We accept payments through Cash App. You can send your deposit and weekly payments to: $easymoneyAL\n\nJust click the Cash App button on our booking page to pay directly!";
    }
    
    // Contact information
    if (msg.includes('contact') || msg.includes('phone') || msg.includes('call') || msg.includes('email') || msg.includes('reach')) {
      return "You can reach us at:\n\n📞 Phone: 215-770-0679\n📧 Email: info@phillyrideshare.com\n\nWe're available Mon-Fri 8am-8pm and Sat-Sun 9am-5pm.";
    }
    
    // Booking questions
    if (msg.includes('book') || msg.includes('reserve') || msg.includes('rent') || msg.includes('how do i')) {
      return "To book a vehicle:\n\n1. Go to our 'Book Now' page\n2. Select your vehicle\n3. Choose your rental dates\n4. Enter your pickup location\n5. Submit your booking request\n6. Pay the $200 deposit via Cash App ($easymoneyAL)\n\nWe'll confirm your booking within 24 hours!";
    }
    
    // Insurance questions
    if (msg.includes('insurance') || msg.includes('insured') || msg.includes('coverage')) {
      return "Yes! All our rentals include comprehensive insurance coverage. You're protected while driving any of our vehicles.";
    }
    
    // Location questions
    if (msg.includes('location') || msg.includes('where') || msg.includes('pickup') || msg.includes('philadelphia') || msg.includes('philly')) {
      return "We're based in Philadelphia and serve the greater Philly area. We can arrange pickup at a convenient location for you. Just enter your preferred pickup address when booking!";
    }
    
    // Rideshare/Uber/Lyft questions
    if (msg.includes('uber') || msg.includes('lyft') || msg.includes('rideshare') || msg.includes('doordash') || msg.includes('delivery')) {
      return "Yes! Our vehicles are perfect for rideshare drivers (Uber, Lyft) and delivery services. Many of our customers use our cars for gig work. All vehicles are well-maintained and fuel-efficient.";
    }
    
    // Duration/length questions
    if (msg.includes('week') || msg.includes('month') || msg.includes('long') || msg.includes('duration') || msg.includes('how long')) {
      return "We offer weekly rentals at $275/week. You can rent for as many weeks as you need - just let us know your desired rental period when booking. Long-term renters are welcome!";
    }
    
    // Hello/greeting
    if (msg.includes('hello') || msg.includes('hi') || msg.includes('hey') || msg.includes('good morning') || msg.includes('good afternoon')) {
      return "Hello! 👋 How can I help you today? Feel free to ask about our vehicles, pricing, requirements, or how to book.";
    }
    
    // Thank you
    if (msg.includes('thank') || msg.includes('thanks')) {
      return "You're welcome! Is there anything else I can help you with? Feel free to call us at 215-770-0679 if you have more questions.";
    }
    
    // Default response
    return "I'd be happy to help! Here are some things I can tell you about:\n\n• Vehicle availability & pricing\n• Rental requirements (age, license)\n• Payment options (Cash App)\n• How to book\n• Contact information\n\nOr call us directly at 215-770-0679!";
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setInputValue('');

    // Simulate typing delay
    setTimeout(() => {
      const botResponse = getBotResponse(userMessage);
      setMessages(prev => [...prev, { type: 'bot', text: botResponse }]);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  const quickQuestions = [
    "What are your prices?",
    "What vehicles do you have?",
    "How do I book?",
    "What are the requirements?"
  ];

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-slate-800 rotate-90' : 'bg-amber-400 hover:bg-amber-300 hover:scale-110'
        }`}
        data-testid="chat-toggle-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-slate-950" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-40 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-slate-950 p-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Fair Rideshare" className="w-10 h-10 rounded-lg" />
              <div>
                <h3 className="font-semibold text-white">Fair Rideshare</h3>
                <p className="text-xs text-amber-400">We typically reply instantly</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.type === 'user'
                      ? 'bg-amber-400 text-slate-950 rounded-br-md'
                      : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{message.text}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 bg-white border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setMessages(prev => [...prev, { type: 'user', text: question }]);
                      setTimeout(() => {
                        const botResponse = getBotResponse(question);
                        setMessages(prev => [...prev, { type: 'bot', text: botResponse }]);
                      }, 500);
                    }}
                    className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-full transition-colors"
                    data-testid={`quick-question-${index}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 rounded-full bg-slate-100 border-0 focus-visible:ring-1 focus-visible:ring-amber-400"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                size="icon"
                className="rounded-full bg-amber-400 hover:bg-amber-300 text-slate-950 h-10 w-10"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
